import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCycle } from "../../context/CycleContext";
import { getManagerDashboard } from "../../services/dashboardApi";
import { getLookups } from "../../services/employeeApi";
import useAsync from "../../hooks/useAsync";
import {
  Card, PageHeader, Loader, ErrorState, DataTable, PersonCell, StatusBadge, ProgressBar, SearchInput,
  OrgFilters, groupByDepartment, countByDepartment,
} from "../../components/common";
import useOrgFilters from "../../hooks/useOrgFilters"; // [NEW] shared organisation filters
import { singleValue } from "../../utils/orgFilters";
import { MAX_GOALS_PER_YEAR } from "../../utils/constants";
import RatingPill from "../../components/performance/RatingPill";

import { formatRatingScore } from "../../utils/ratingScale"; // [NEW] PM-13: score on the chosen scale

const api = { getManagerDashboard, getLookups };

// PM-17: managers do not assign goals, so the "Assign goal" button is removed from this list.

export default function TeamPage() {
  const { cycleId, cycle } = useCycle();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const org = useOrgFilters(); // [NEW] Business Unit, Department, Location, Cost Centre, Legal Entity
  const { data, error, reload } = useAsync(() => api.getManagerDashboard(cycleId), [cycleId], { skip: !cycleId });
  const lookups = useAsync(() => api.getLookups(), []);

  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (!data) return <Loader />;

  const q = search.toLowerCase();
  const searched = data.team.filter((t) => !q || t.employee.name.toLowerCase().includes(q) || t.employee.designationName.toLowerCase().includes(q));
  const rows = searched.filter((t) => org.matches(t.employee));
  const counts = countByDepartment(data.team);
  const departments = (lookups.data?.departments || []).filter((d) => counts[d.id]);
  const oneDept = singleValue(org.filters.departmentId); // PM-18: several departments are grouped as usual
  const groups = oneDept ? [{ id: oneDept, name: departments.find((d) => d.id === oneDept)?.name || "Department", rows }] : groupByDepartment(rows);

  const table = (groupRows) => (
        <DataTable
          rows={groupRows}
          rowKey={(r) => r.employee.id}
          onRowClick={(r) => navigate(`/team/${r.employee.id}`)}
          emptyTitle="No team members found"
          columns={[
            { key: "name", label: "Name", render: (r) => <PersonCell name={r.employee.name} meta={`${r.employee.id}, ${r.employee.designationName}`} /> },
            { key: "department", label: "Department", render: (r) => r.employee.departmentName },
            { key: "location", label: "Location", render: (r) => r.employee.location },
            { key: "sheet", label: "Goals", render: (r) => <StatusBadge status={r.sheetStatus} /> },
            { key: "count", label: "Goals set", align: "right", render: (r) => `${r.goalCount ?? 0} / ${MAX_GOALS_PER_YEAR}` },
            { key: "completion", label: "Completion", width: 150, render: (r) => <ProgressBar value={r.goalCompletion} showValue size="sm" /> },
            { key: "self", label: "Self assessment", render: (r) => <StatusBadge status={r.selfStatus} /> },
            { key: "score", label: "Score", align: "right", render: (r) => formatRatingScore(r.performance.finalScore) },
            { key: "rating", label: "Rating", render: (r) => <RatingPill rating={r.resultStatus === "Awaiting Assessments" ? null : r.performance.rating} tone={r.performance.ratingTone} size="sm" /> },
          ]}
        />
  );

  return (
    <div className="page">
      <PageHeader
        title="Team members"
        subtitle={`${data.teamSize} direct reports${departments.length > 1 ? ` in ${departments.length} departments` : ""}. Status for ${cycle?.name}.`}
        actions={
          <div className="page-filters">
            <OrgFilters org={org} />
            <SearchInput value={search} onChange={setSearch} placeholder="Search by name or role" />
          </div>
        }
      />
      {!groups.length && <Card padded={false}>{table([])}</Card>}
      {groups.map((g) => (
        <Card key={g.id} title={g.name} subtitle={`${g.rows.length} ${g.rows.length === 1 ? "person" : "people"}`} padded={false}>
          {table(g.rows)}
        </Card>
      ))}
    </div>
  );
}
